import classnames from 'classnames/bind';
import { isFunction, isString } from 'lodash/fp';
import {
    cloneElement,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { flushSync } from 'react-dom';
import {
    arrow as arrowMiddleware,
    autoUpdate,
    flip as flipMiddleware,
    FloatingArrow,
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingTree,
    offset as offsetMiddleware,
    safePolygon,
    shift as shiftMiddleware,
    size as sizeMiddleware,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFloatingTree,
    useHover,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
    useTypeahead
} from '@floating-ui/react';

import { Theme } from 'app/lib/constants';
import { createVirtualElement, resolveReferenceElement } from 'app/lib/utils';
import { Icon, IconSize } from 'app/components/core/Icon';
import { ThemeProvider, useTheme } from 'app/components/core/ThemeProvider';

import { MenuContext } from './MenuContext.jsx';

import * as styles from './ComposableMenu.css';

const cx = classnames.bind(styles);

function SubMenuItem({ children, ref, ...props }) {
    return (
        <button role="menuitem" className={cx('menu-item')} ref={ref} {...props}>
            <span className={cx('label')}>{children}</span>
            <Icon
                className={cx('chevron')}
                variant="chevron-right-big"
                size={IconSize.EXTRA_SMALL}
                inline
            />
        </button>
    );
}

function DropdownMenuComponent({
    className,
    trigger,
    position,
    isOpen: controlledIsOpen,
    onOpenChange,
    label,
    placement = 'bottom-start',
    hoverDelay = 75,
    mainAxisOffset = 4,
    alignmentAxisOffset = 0,
    arrow = false,
    openOnHover = false,
    width = 'auto',
    parentTheme,
    children,
    ref,
    ...props
}) {
    const { themeClassname } = useTheme();
    const isContextMenu = position !== undefined;
    const isStringTrigger = isString(trigger);
    const isControlledMenu = isContextMenu || isStringTrigger;
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = isControlledMenu ? controlledIsOpen : internalIsOpen;

    const setIsOpen = useCallback(
        (value) => {
            if (!isControlledMenu) {
                setInternalIsOpen(value);
            }
            onOpenChange?.(value);
        },
        [isControlledMenu, onOpenChange]
    );

    const [activeIndex, setActiveIndex] = useState(null);
    const [maxHeight, setMaxHeight] = useState(null);

    const arrowRef = useRef(null);
    const elementsRef = useRef([]);
    const labelsRef = useRef([]);
    const parent = useContext(MenuContext);

    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const item = useListItem();

    const isNested = parentId !== null;

    const { floatingStyles, refs, context, elements } = useFloating({
        nodeId,
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: isNested ? 'right-start' : placement,
        middleware: [
            offsetMiddleware({
                mainAxis: isNested ? 4 : mainAxisOffset,
                alignmentAxis: isNested ? -8 : alignmentAxisOffset
            }),
            flipMiddleware(),
            shiftMiddleware(),
            sizeMiddleware({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight - 32));
                }
            }),
            arrowMiddleware({ element: arrowRef })
        ],
        whileElementsMounted: isContextMenu ? undefined : autoUpdate
    });

    const hover = useHover(context, {
        enabled: !isControlledMenu && (openOnHover || isNested),
        delay: { open: hoverDelay },
        handleClose: safePolygon({ blockPointerEvents: true })
    });

    const click = useClick(context, {
        enabled: !isControlledMenu,
        event: 'mousedown',
        toggle: !isNested,
        ignoreMouse: isNested
    });

    const role = useRole(context, { role: 'menu' });

    const dismiss = useDismiss(context, { bubbles: true });

    const listNavigation = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        nested: isNested,
        onNavigate: setActiveIndex
    });

    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        onMatch: isOpen ? setActiveIndex : undefined,
        activeIndex
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        hover,
        click,
        role,
        dismiss,
        listNavigation,
        typeahead
    ]);

    useEffect(() => {
        if (!tree) {
            return;
        }

        function handleTreeClick() {
            setIsOpen(false);
        }

        function onSubMenuOpen(event) {
            if (event.nodeId !== nodeId && event.parentId === parentId) {
                setIsOpen(false);
            }
        }

        tree.events.on('click', handleTreeClick);
        tree.events.off('menuopen', onSubMenuOpen);

        return () => {
            tree.events.off('click', handleTreeClick);
            tree.events.off('menuopen', onSubMenuOpen);
        };
    }, [tree, nodeId, parentId, setIsOpen]);

    useLayoutEffect(() => {
        if (position) {
            refs.setPositionReference(createVirtualElement(position));
        }
    }, [position, refs]);

    useLayoutEffect(() => {
        if (isStringTrigger) {
            const element = resolveReferenceElement(trigger);
            if (element) {
                refs.setReference(element);
            }
        }
    }, [trigger, isStringTrigger, refs]);

    useEffect(() => {
        if (isOpen && tree) {
            tree.events.emit('menuopen', { parentId, nodeId });
        }
    }, [tree, isOpen, nodeId, parentId]);

    useEffect(() => {
        if (elements.floating) {
            try {
                elements.floating?.showPopover();
            } catch {
                // Ignore error. We might be trying to call this on an element
                // that's being removed from the DOM.
            }
        }
    }, [elements.floating]);

    const triggerRef = useMergeRefs([refs.setReference, item.ref, ref]);

    const triggerProps = {
        ref: triggerRef,
        tabIndex: !isNested ? undefined : parent.activeIndex === item.index ? 0 : -1,
        ...getReferenceProps(
            parent.getItemProps({
                ...props,
                onFocus(event) {
                    props.onFocus?.(event);
                }
            })
        )
    };

    let triggerElement = null;
    if (!isControlledMenu) {
        if (isFunction(trigger)) {
            triggerElement = trigger({ isOpen, ...triggerProps });
        } else {
            triggerElement = cloneElement(trigger ?? <SubMenuItem />, triggerProps, label);
        }
    }

    return (
        <FloatingNode id={nodeId}>
            {triggerElement &&
                (parentTheme ? (
                    <ThemeProvider theme={parentTheme}>{triggerElement}</ThemeProvider>
                ) : (
                    triggerElement
                ))}
            <MenuContext.Provider
                value={{
                    activeIndex,
                    setActiveIndex,
                    getItemProps,
                    isOpen
                }}>
                <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                    {isOpen && (
                        <FloatingFocusManager
                            context={context}
                            modal={false}
                            initialFocus={isNested ? -1 : 0}
                            returnFocus={isContextMenu ? false : !isNested}>
                            <div
                                popover="manual"
                                ref={refs.setFloating}
                                className={cx('menu', themeClassname, className)}
                                style={floatingStyles}
                                {...getFloatingProps({
                                    onClick(event) {
                                        event.stopPropagation();
                                    }
                                })}>
                                {arrow && (
                                    <FloatingArrow
                                        className={cx('arrow')}
                                        ref={arrowRef}
                                        context={context}
                                        d="M0,14 L6.125,7.825 Q7,7,7.825,7.825 L14,14"
                                        strokeWidth="1"
                                    />
                                )}
                                <div style={{ width, maxHeight }} className={cx('menu-items')}>
                                    {children}
                                </div>
                            </div>
                        </FloatingFocusManager>
                    )}
                </FloatingList>
            </MenuContext.Provider>
        </FloatingNode>
    );
}

const DefaultTrigger = <button />;

export function DropdownMenu({ trigger = DefaultTrigger, theme = Theme.LIGHT, ...props }) {
    const parentId = useFloatingParentNodeId();

    // We need to get the "parent theme" and re-apply it to the root trigger
    // button, otherwise the trigger button will inherit the theme of the
    // menu itself.
    const { theme: parentTheme } = useTheme();

    // Sub menu
    if (parentId) {
        return <DropdownMenuComponent {...props} />;
    }

    return (
        <ThemeProvider theme={theme}>
            <FloatingTree>
                <DropdownMenuComponent trigger={trigger} parentTheme={parentTheme} {...props} />
            </FloatingTree>
        </ThemeProvider>
    );
}

export function ContextMenu({ x, y, isOpen = true, theme = Theme.LIGHT, ...props }) {
    const position = useMemo(() => ({ x, y }), [x, y]);

    return (
        <ThemeProvider theme={theme}>
            <FloatingTree>
                <DropdownMenuComponent position={position} isOpen={isOpen} {...props} />
            </FloatingTree>
        </ThemeProvider>
    );
}
